import React, { useCallback, useEffect, useState } from "react";
import { getAllTransactions } from "../../business/PaymentBusiness";
import { getAllUsers } from "../../business/UserBusiness";
import { Sidebar } from "../../components/Sidebar";
import {
  FaCalendarAlt,
  FaChartLine,
  FaCreditCard,
  FaGoogle,
  FaMoneyBillWave,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";
import { MdTrendingUp } from "react-icons/md";
import toast from "react-hot-toast";

const FINANCIAL_TRANSACTION_TYPES = ["transaction", "recurring_bill", "installment_bill"];
const APPOINTMENT_TYPES = ["calendar"];

export const Reports = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState({
    totalTransacted: 0,
    totalTransactions: 0,
    totalAppointments: 0,
    averageTransactionValue: 0,
    mostActiveMonth: "",
    mostActiveDay: "",
    topCategories: [],
    topAppointmentCategories: [],
    userGrowth: 0,
    activeUsers: 0,
    premiumUsers: 0,
    googleIntegratedUsers: 0,
    sharedAccounts: 0,
  });

  const adminName = localStorage.getItem("@Auth:name");

  const calculateMetrics = useCallback((transactionsData, usersData) => {
    const safeTransactions = Array.isArray(transactionsData) ? transactionsData : [];
    const safeUsers = Array.isArray(usersData) ? usersData : [];

    const financialTransactions = safeTransactions.filter((transaction) =>
      FINANCIAL_TRANSACTION_TYPES.includes(transaction?.type)
    );
    const appointments = safeTransactions.filter((transaction) =>
      APPOINTMENT_TYPES.includes(transaction?.type)
    );

    const totalTransacted = financialTransactions.reduce((sum, transaction) => {
      return sum + (parseFloat(transaction?.value) || 0);
    }, 0);

    const totalTransactions = financialTransactions.length;
    const totalAppointments = appointments.length;
    const averageTransactionValue = totalTransactions > 0 ? totalTransacted / totalTransactions : 0;

    const monthlyStats = {};
    safeTransactions.forEach((transaction) => {
      const referenceDate = transaction?.createdAt || transaction?.date;
      if (!referenceDate) return;

      const date = new Date(referenceDate);
      if (Number.isNaN(date.getTime())) return;

      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
      monthlyStats[monthKey] = (monthlyStats[monthKey] || 0) + 1;
    });

    const mostActiveMonth =
      Object.keys(monthlyStats).length > 0
        ? Object.keys(monthlyStats).reduce((a, b) => (monthlyStats[a] > monthlyStats[b] ? a : b), "")
        : "";

    const dailyStats = {};
    safeTransactions.forEach((transaction) => {
      const referenceDate = transaction?.createdAt || transaction?.date;
      if (!referenceDate) return;

      const date = new Date(referenceDate);
      if (Number.isNaN(date.getTime())) return;

      const dayOfWeek = date.getDay();
      dailyStats[dayOfWeek] = (dailyStats[dayOfWeek] || 0) + 1;
    });

    const dayNames = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
    const mostActiveDay =
      Object.keys(dailyStats).length > 0
        ? dayNames[Object.keys(dailyStats).reduce((a, b) => (dailyStats[a] > dailyStats[b] ? a : b), "")]
        : "";

    const categoryStats = {};
    financialTransactions.forEach((transaction) => {
      const category = transaction?.category || "Outros";
      categoryStats[category] = (categoryStats[category] || 0) + 1;
    });

    const topCategories = Object.entries(categoryStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([category, count]) => ({ category, count }));

    const appointmentCategoryStats = {};
    appointments.forEach((appointment) => {
      const category = appointment?.category || "Outros";
      appointmentCategoryStats[category] = (appointmentCategoryStats[category] || 0) + 1;
    });

    const topAppointmentCategories = Object.entries(appointmentCategoryStats)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5)
      .map(([category, count]) => ({ category, count }));

    const activeUsers = safeUsers.filter((user) => user?.subscription === "active").length;
    const premiumUsers = safeUsers.filter((user) => user?.role === "patner").length;
    const googleIntegratedUsers = safeUsers.filter(
      (user) => user?.google && Object.keys(user.google).length > 0
    ).length;
    const sharedAccounts = safeUsers.filter(
      (user) => user?.sharedAccounts && Object.keys(user.sharedAccounts).length > 0
    ).length;

    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const recentUsers = safeUsers.filter(
      (user) => user?.createdAt && new Date(user.createdAt) > lastMonth
    ).length;

    setMetrics({
      totalTransacted,
      totalTransactions,
      totalAppointments,
      averageTransactionValue,
      mostActiveMonth,
      mostActiveDay,
      topCategories,
      topAppointmentCategories,
      userGrowth: recentUsers,
      activeUsers,
      premiumUsers,
      googleIntegratedUsers,
      sharedAccounts,
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [transactionsResponse, usersResponse] = await Promise.all([
        getAllTransactions(),
        getAllUsers(),
      ]);

      let transactionsData = [];
      if (Array.isArray(transactionsResponse?.data?.transaction)) {
        transactionsData = transactionsResponse.data.transaction;
      } else if (Array.isArray(transactionsResponse?.data?.transactions)) {
        transactionsData = transactionsResponse.data.transactions;
      } else if (Array.isArray(transactionsResponse?.data)) {
        transactionsData = transactionsResponse.data;
      } else if (Array.isArray(transactionsResponse)) {
        transactionsData = transactionsResponse;
      }

      const usersData = Array.isArray(usersResponse?.data) ? usersResponse.data : [];

      setUsers(usersData);
      calculateMetrics(transactionsData, usersData);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar relatórios");
    } finally {
      setIsLoading(false);
    }
  }, [calculateMetrics]);

  const formatCurrency = (amount) => {
    return amount.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatMonth = (monthKey) => {
    if (!monthKey) return "N/A";
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
    return date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <>
      <div className="flex flex-col lg:flex-row">
        <Sidebar />
        <div className="flex-1 min-h-screen bg-white">
          <div className="max-w-7xl w-full mx-auto py-6 px-2 sm:px-4">
            <div className="mb-6">
              <h1 className="text-lg lg:text-xl font-light leading-6">
                Olá, <span className="font-bold">{adminName}</span>
              </h1>
              <p className="text-base lg:text-lg font-light leading-6 text-gray-700">
                Relatórios Gerais - Visão Geral do Sistema
              </p>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Carregando relatórios...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-90">Total Transacionado</p>
                        <p className="text-2xl font-bold">{formatCurrency(metrics.totalTransacted)}</p>
                      </div>
                      <FaMoneyBillWave className="text-3xl opacity-80" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-90">Transações Financeiras</p>
                        <p className="text-2xl font-bold">{metrics.totalTransactions}</p>
                      </div>
                      <FaCreditCard className="text-3xl opacity-80" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-90">Agendamentos</p>
                        <p className="text-2xl font-bold">{metrics.totalAppointments}</p>
                      </div>
                      <FaCalendarAlt className="text-3xl opacity-80" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-90">Usuários Ativos</p>
                        <p className="text-2xl font-bold">{metrics.activeUsers}</p>
                      </div>
                      <FaUsers className="text-3xl opacity-80" />
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium opacity-90">Google Integrado</p>
                        <p className="text-2xl font-bold">{metrics.googleIntegratedUsers}</p>
                      </div>
                      <FaGoogle className="text-3xl opacity-80" />
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white mb-8">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium opacity-90">Contas compartilhadas</p>
                      <p className="text-2xl font-bold">{metrics.sharedAccounts}</p>
                    </div>
                    <FaUsers className="text-3xl opacity-80" />
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                      <FaChartLine className="text-indigo-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Valor Médio por Transação</h3>
                    </div>
                    <p className="text-3xl font-bold text-indigo-600">
                      {formatCurrency(metrics.averageTransactionValue)}
                    </p>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                      <MdTrendingUp className="text-green-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Novos Usuários (Último Mês)</h3>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{metrics.userGrowth}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                      <FaTrophy className="text-yellow-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Usuários Parceiros</h3>
                    </div>
                    <p className="text-3xl font-bold text-yellow-600">{metrics.premiumUsers}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                      <FaCalendarAlt className="text-blue-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Mês Mais Ativo</h3>
                    </div>
                    <p className="text-xl font-semibold text-blue-600">{formatMonth(metrics.mostActiveMonth)}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                      <FaCalendarAlt className="text-purple-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Dia Mais Ativo</h3>
                    </div>
                    <p className="text-xl font-semibold text-purple-600">{metrics.mostActiveDay}</p>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-4">
                      <FaUsers className="text-gray-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Total de Usuários</h3>
                    </div>
                    <p className="text-xl font-semibold text-gray-600">{users.length}</p>
                  </div>
                </div>

                <div className="mt-8">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-6">
                      <FaCreditCard className="text-blue-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Categorias de Transações Financeiras</h3>
                    </div>

                    {metrics.topCategories.length > 0 ? (
                      <div className="space-y-4">
                        {metrics.topCategories.map((category, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <span className="text-lg font-bold text-blue-600 mr-4">#{index + 1}</span>
                              <span className="font-medium text-gray-900">{category.category}</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-600 bg-white px-3 py-1 rounded-full">
                              {category.count} transações
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">Nenhuma transação financeira encontrada</p>
                    )}
                  </div>
                </div>

                <div className="mt-8">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex items-center mb-6">
                      <FaCalendarAlt className="text-indigo-600 text-xl mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">Categorias de Agendamentos</h3>
                    </div>

                    {metrics.topAppointmentCategories.length > 0 ? (
                      <div className="space-y-4">
                        {metrics.topAppointmentCategories.map((category, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                            <div className="flex items-center">
                              <span className="text-lg font-bold text-indigo-600 mr-4">#{index + 1}</span>
                              <span className="font-medium text-gray-900">{category.category}</span>
                            </div>
                            <span className="text-sm font-semibold text-gray-600 bg-white px-3 py-1 rounded-full">
                              {category.count} agendamentos
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-8">Nenhum agendamento encontrado</p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
